import { UserPlus } from 'lucide-react'
import { useCallback, useState } from 'react'

import { CollapsibleDataContainer } from 'components/reusable/CollapsibleDataContainer'
import { Banner, TextLink } from 'slab'
import { type AdminComplianceOwner } from 'utils/web_api.schemas'

import EmployerComplianceOwnerSheet from './EmployerComplianceOwnerSheet'
import EmployerComplianceOwnersTable from './EmployerComplianceOwnersTable'

const Heading = ({ onAddOwnerClick }: { onAddOwnerClick: () => void }) => (
  <div className='flex items-center justify-between'>
    <div className='flex items-center space-x-2'>
      <span className='heading-md'>Owners</span>
      <div className='cursor-pointer' onClick={(e) => e.stopPropagation()}>
        <TextLink color='purple' leftIcon={UserPlus} onClick={onAddOwnerClick}>
          Add Owner
        </TextLink>
      </div>
    </div>
  </div>
)

interface EmployerOwnersViewProps {
  employerId: string
}

const EmployerOwnersView = ({ employerId }: EmployerOwnersViewProps) => {
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const [selectedOwner, setSelectedOwner] = useState<AdminComplianceOwner | undefined>(undefined)

  const handleAddOwnerClick = useCallback(() => {
    setIsSheetOpen(true)
    setSelectedOwner(undefined)
  }, [])
  const successContent = (
    <div className='p-2'>
      <div className='mt-4 mb-4 p-3 bg-gray-50 rounded-md border border-gray-200'>
        <span className='font-bold text-md'>👥 What is this?</span>
        <br />
        <span>
          An Owner can fall under one or both of the following: ownership (25%+ equity) or control (such as a CEO, CFO, COO). We must identify any Owners with more than 25% ownership, at least one Owner with significant control, and then run KYC checks on these Owners to fulfill regulatory obligations to our bank partners.
        </span>
      </div>
      <EmployerComplianceOwnersTable employerId={employerId} setIsSheetOpen={setIsSheetOpen} setSelectedOwner={setSelectedOwner} />
      <EmployerComplianceOwnerSheet
        isOpen={isSheetOpen}
        setIsOpen={setIsSheetOpen}
        setSelectedOwner={setSelectedOwner}
        employerId={employerId}
        owner={selectedOwner}
      />
    </div>
  )

  const errorContent = (
    <Banner title='Error loading employer owners. Please try again later.' />
  )

  return (
    <CollapsibleDataContainer
      heading={
        <Heading onAddOwnerClick={handleAddOwnerClick} />
      }
      isLoading={false}
      error={false}
      localStorageKey='employer-owners-view'
      loadingContent={<div>Loading employer owners...</div>}
      errorContent={errorContent}
      successContent={successContent}
    />
  )
}

export default EmployerOwnersView
